<!--
@component
Trophies

See markup below.
-->

<script lang="ts">
  import { Block } from '@reuters-graphics/graphics-components';
  import { getTrophies } from '$utils/data';
  import Star from './Star.svelte';

  /** Shape of each entry in `trophies.json` we actually consume. */
  type Trophy = {
    winnerContestantId?: string;
    runnerUpContestantId?: string;
    tournamentCalendarName?: string;
    [key: string]: unknown;
  };

  let { teamId }: { teamId: string } = $props();

  /**
   * `undefined` → not loaded yet
   * `null`      → fetch failed; render nothing
   * `Trophy[]`  → loaded successfully
   */
  let pastWinners = $state<Trophy[] | null | undefined>(undefined);

  let pastTrophiesForTeam: Trophy[] = $derived(
    Array.isArray(pastWinners) ?
      pastWinners.filter(
        (p) =>
          p.winnerContestantId === teamId || p.runnerUpContestantId === teamId
      )
    : []
  );

  /**
   * Extract the year from strings like `'2022 World Cup'` or `'2022Z'`. Opta
   * isn't consistent here — sometimes the year is embedded mid-string, so
   * we look for any 4-digit run before falling back to the first 4 chars.
   */
  function getYear(input: string | undefined): string {
    if (!input) return '';
    const match = input.match(/\d{4}/);
    return match ? match[0] : input.slice(0, 4);
  }

  $effect(() => {
    // Track whether the effect was cleaned up (e.g. user navigated away)
    // before the async fetch resolved, so we don't write to state that no
    // longer matters.
    let cancelled = false;

    (async () => {
      // `getTrophies` routes through the shared data layer (local fixtures
      // in dev/preview, S3 in production) and returns `null` on failure.
      const data = await getTrophies();
      if (cancelled) return;
      pastWinners = (data as any)?.competition?.[0]?.trophy ?? null;
    })();

    return () => {
      cancelled = true;
    };
  });
</script>

{#if pastTrophiesForTeam.length > 0}
  <div class="container">
    <Block class="fmy-0" width="normal">
      <div class="flex trophy-cabinet" role="list" aria-label="Trophy cabinet">
        {#each pastTrophiesForTeam as pastTrophy, i (`${pastTrophy.tournamentCalendarName ?? i}-${pastTrophy.winnerContestantId === teamId ? 'w' : 'r'}`)}
          {@const isWinner = pastTrophy.winnerContestantId === teamId}
          <Star
            year={getYear(pastTrophy.tournamentCalendarName)}
            place={isWinner ? 'Winner' : 'Runner-up'}
          />
        {/each}
      </div>
    </Block>
  </div>
{/if}

<style lang="scss">
  .container {
    margin-top: -2rem;
    margin-bottom: -1.8rem;
  }

  .trophy-cabinet {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 15px;
    justify-content: center;
  }
</style>
